import type { ConversationMessage } from '../types';

type ThreadMessageRef = Pick<
  ConversationMessage,
  'id' | 'created_at' | 'role' | 'parent_message_id'
>;

function compareMessageChronology(left: ThreadMessageRef, right: ThreadMessageRef) {
  const createdAtDiff =
    new Date(left.created_at).getTime() - new Date(right.created_at).getTime();
  if (createdAtDiff !== 0) {
    return createdAtDiff;
  }
  return left.id.localeCompare(right.id);
}

export function sortConversationMessagesForDisplay<T extends ThreadMessageRef>(
  messages: readonly T[],
  queuedMessageOrder: readonly string[]
): T[] {
  if (messages.length <= 1 || queuedMessageOrder.length === 0) {
    return [...messages].sort(compareMessageChronology);
  }

  const queueIndexByMessageId = new Map(
    queuedMessageOrder.map((messageId, index) => [messageId, index])
  );

  const resolveQueuedTurnIndex = (message: ThreadMessageRef) => {
    if (message.role === 'user') {
      return queueIndexByMessageId.get(message.id) ?? null;
    }
    if (message.role === 'assistant' && message.parent_message_id) {
      return queueIndexByMessageId.get(message.parent_message_id) ?? null;
    }
    return null;
  };

  return [...messages].sort((left, right) => {
    const leftQueueIndex = resolveQueuedTurnIndex(left);
    const rightQueueIndex = resolveQueuedTurnIndex(right);
    if (leftQueueIndex !== null && rightQueueIndex !== null) {
      if (leftQueueIndex !== rightQueueIndex) {
        return leftQueueIndex - rightQueueIndex;
      }
      if (left.role !== right.role) {
        return left.role === 'user' ? -1 : 1;
      }
    }
    return compareMessageChronology(left, right);
  });
}
